/**
 * Portal Manta · CriterioSimilaridadePage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S } from '../../globals.js';
import { F } from '../../tokens/fonts.js';

const CriterioSimilaridadePage = memo(function CriterioSimilaridadePage_Inner(){
  const KEY = 'manta-criterio-similar-v1';
  const carregar = () => {
    if (typeof window === 'undefined') return {pesoFis:0.7, pesoCtx:0.3, ctxL3:{r:0.65,u:0.20,s:0.15}, ctxL4:{r:0.55,u:0.30,s:0.15}, ctxL5:{r:0.60,u:0.10,s:0.30}};
    try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch(e){ return {}; }
  };
  const inicial = carregar();
  const [pesoFis, setPesoFis] = useState(inicial.pesoFis ?? 0.7);
  const [ctxL3, setCtxL3] = useState(inicial.ctxL3 ?? {r:0.65,u:0.20,s:0.15});
  const [ctxL4, setCtxL4] = useState(inicial.ctxL4 ?? {r:0.55,u:0.30,s:0.15});
  const [ctxL5, setCtxL5] = useState(inicial.ctxL5 ?? {r:0.60,u:0.10,s:0.30});

  const pesoCtx = 1 - pesoFis;

  const salvar = () => {
    if (typeof window === 'undefined') return;
    const conf = {pesoFis, pesoCtx, ctxL3, ctxL4, ctxL5};
    localStorage.setItem(KEY, JSON.stringify(conf));
    window.alert('Calibração salva. Os novos pesos serão aplicados no Novo Orçamento (aba Estimar).');
  };

  const resetar = () => {
    if (!window.confirm('Restaurar valores padrão da calibração?')) return;
    setPesoFis(0.7);
    setCtxL3({r:0.65,u:0.20,s:0.15});
    setCtxL4({r:0.55,u:0.30,s:0.15});
    setCtxL5({r:0.60,u:0.10,s:0.30});
  };

  const inputStl = {fontSize:11.5,padding:'5px 8px',border:'1px solid '+V.b1,borderRadius:4,outline:'none',width:'100%',fontFamily:'ui-monospace,monospace'};
  const labelStl = {fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3,marginBottom:4,textTransform:'uppercase'};

  const corLote = (sig) => ({'3':V.gn, '4':V.bl, '5':V.or})[sig] || V.gr;

  return(<div style={S.page}>
    <div style={S.h1}>🎯 Critério de Similaridade — algoritmo + calibração</div>
    <div style={S.sub}>Cosseno em features normalizadas · pesos físico vs contexto · classificação dos lotes</div>

    {/* Quadro 1 · Como funciona */}
    <div style={{...S.cardP,padding:18,marginBottom:14}}>
      <div style={{fontSize:11,fontWeight:800,color:V.br,textTransform:'uppercase',letterSpacing:.05,marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
        <span style={{width:22,height:22,background:V.br,color:'#fff',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800}}>?</span>
        Como funciona o cosseno
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:18}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8}}>Fórmula</div>
          <div style={{padding:'12px 14px',background:V.g0,border:'1px solid '+V.g1,borderRadius:6,fontFamily:'monospace',fontSize:12,color:V.br,lineHeight:1.7}}>
            cos(A,B) = <b>(A · B)</b> / (|A| × |B|)<br/>
            <span style={{fontSize:10,color:V.gr}}>onde A·B é produto escalar, |A|=√Σa²</span>
          </div>
          <div style={{fontSize:10.5,color:V.gr,marginTop:10,lineHeight:1.6}}>
            <b style={{color:V.br}}>Resultado:</b> 1.0 = idênticos · 0.0 = ortogonais · −1.0 = opostos.<br/>
            Cosseno é <b>invariante a escala</b> — mede o ângulo entre vetores, ignora magnitude.
          </div>
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8}}>Score combinado</div>
          <div style={{padding:'12px 14px',background:V.g0,border:'1px solid '+V.g1,borderRadius:6,fontFamily:'monospace',fontSize:12,color:V.br,lineHeight:1.7}}>
            score = <b>{(pesoFis*100).toFixed(0)}%</b> × cos<sub>físico</sub> + <b>{(pesoCtx*100).toFixed(0)}%</b> × cos<sub>contexto</sub>
          </div>
          <div style={{fontSize:10.5,color:V.gr,marginTop:10,lineHeight:1.6}}>
            <b style={{color:V.br}}>Físico:</b> densidades (dup/km, OAE/km, marg/km, cicl/km, inter/km).<br/>
            <b style={{color:V.br}}>Contexto:</b> classificação categórica rural/urbano/serra (somam ~1).
          </div>
        </div>
      </div>
    </div>

    {/* Quadro 2 · Tuning de pesos */}
    <div style={{...S.cardP,padding:18,marginBottom:14}}>
      <div style={{fontSize:11,fontWeight:800,color:V.or,textTransform:'uppercase',letterSpacing:.05,marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
        <span style={{width:22,height:22,background:V.or,color:'#fff',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800}}>⚖</span>
        Tuning · pesos físico vs contexto
      </div>
      <div style={{padding:'14px 16px',background:V.g0,border:'1px solid '+V.g1,borderRadius:6}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:8}}>
          <span style={{fontSize:11,fontWeight:700,color:V.gr}}>PESO FÍSICO ({(pesoFis*100).toFixed(0)}%)</span>
          <span style={{fontSize:11,fontWeight:700,color:V.gr}}>PESO CONTEXTO ({(pesoCtx*100).toFixed(0)}%)</span>
        </div>
        <input type="range" min="0" max="1" step="0.05" value={pesoFis} onChange={e=>setPesoFis(parseFloat(e.target.value))} style={{width:'100%'}}/>
        <div style={{fontSize:9.5,color:V.gr,marginTop:8,fontStyle:'italic',lineHeight:1.5}}>
          <b style={{color:V.br}}>Padrão 70/30:</b> dá mais peso às quantidades físicas, mas considera contexto.<br/>
          <b style={{color:V.br}}>50/50:</b> balanço perfeito · físico e contexto pesam igual.<br/>
          <b style={{color:V.br}}>100/0:</b> só features físicas (similaridade pura de quantidades).<br/>
          <b style={{color:V.br}}>0/100:</b> só contexto (similaridade puramente categórica).
        </div>
      </div>
    </div>

    {/* Quadro 3 · Calibração de contexto dos lotes */}
    <div style={{...S.cardP,padding:18,marginBottom:14}}>
      <div style={{fontSize:11,fontWeight:800,color:V.bl,textTransform:'uppercase',letterSpacing:.05,marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
        <span style={{width:22,height:22,background:V.bl,color:'#fff',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800}}>L</span>
        Calibração do contexto dos lotes oficiais
      </div>
      <div style={{fontSize:10,color:V.gr,marginBottom:14,fontStyle:'italic',lineHeight:1.5}}>
        Classificação categórica do contexto de cada lote (rural / urbano / serra). Idealmente os 3 valores devem somar 1. Estes valores são usados no cálculo de similaridade do contexto.
      </div>
      {[
        {sig:'3', state:ctxL3, set:setCtxL3, nome:'Lote 3 · BR-376'},
        {sig:'4', state:ctxL4, set:setCtxL4, nome:'Lote 4 · PR-323'},
        {sig:'5', state:ctxL5, set:setCtxL5, nome:'Lote 5 · BR-369'},
      ].map(l => (
        <div key={l.sig} style={{padding:'12px 14px',background:V.g0,border:'1px solid '+V.g1,borderRadius:6,marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:700,color:corLote(l.sig),marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
            <span style={{display:'inline-block',width:10,height:10,borderRadius:99,background:corLote(l.sig)}}/>
            {l.nome}
            <span style={{marginLeft:'auto',fontSize:9.5,color:V.gr,fontWeight:500,fontStyle:'italic'}}>
              soma: {((l.state.r+l.state.u+l.state.s)*100).toFixed(0)}%
              {Math.abs(l.state.r+l.state.u+l.state.s - 1) > 0.05 && <span style={{color:V.or,marginLeft:6}}>⚠</span>}
            </span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14}}>
            <div>
              <div style={labelStl}>Rural ({(l.state.r*100).toFixed(0)}%)</div>
              <input type="range" min="0" max="1" step="0.05" value={l.state.r} onChange={e=>l.set({...l.state,r:parseFloat(e.target.value)})} style={{width:'100%'}}/>
            </div>
            <div>
              <div style={labelStl}>Urbano ({(l.state.u*100).toFixed(0)}%)</div>
              <input type="range" min="0" max="1" step="0.05" value={l.state.u} onChange={e=>l.set({...l.state,u:parseFloat(e.target.value)})} style={{width:'100%'}}/>
            </div>
            <div>
              <div style={labelStl}>Serra ({(l.state.s*100).toFixed(0)}%)</div>
              <input type="range" min="0" max="1" step="0.05" value={l.state.s} onChange={e=>l.set({...l.state,s:parseFloat(e.target.value)})} style={{width:'100%'}}/>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Botões */}
    <div style={{display:'flex',gap:10,alignItems:'center'}}>
      <button onClick={salvar} style={{padding:'10px 24px',fontSize:12,fontWeight:800,background:V.gn,color:'#fff',border:'none',borderRadius:6,cursor:'pointer',boxShadow:'0 2px 6px rgba(22,163,74,.25)'}}>💾 Salvar calibração</button>
      <button onClick={resetar} style={{padding:'10px 18px',fontSize:11.5,fontWeight:700,background:'#fff',color:V.gr,border:'1.5px solid '+V.b1,borderRadius:6,cursor:'pointer'}}>↺ Restaurar padrão</button>
      <span style={{fontSize:9.5,color:V.gr,fontStyle:'italic',marginLeft:10}}>Salvo em localStorage · aplicado no Novo Orçamento (Estimar)</span>
    </div>

    {/* Nota metodológica */}
    <div style={{padding:'12px 14px',background:V.g0,borderRadius:6,border:'1px solid '+V.g1,fontSize:9.5,color:V.gr,lineHeight:1.5,marginTop:14}}>
      <b style={{color:V.br}}>Por que cosseno e não distância euclidiana?</b> Cosseno é invariante a escala — projetos de tamanho diferente podem ter perfis muito similares. Distância euclidiana penaliza diferenças de magnitude, o que é ruim aqui.<br/>
      <b style={{color:V.br}}>Por que densidades?</b> Para que a similaridade não seja dominada pela extensão. Lote de 100 km com 50 OAEs é muito similar a lote de 200 km com 100 OAEs (mesmo perfil).<br/>
      <b style={{color:V.br}}>Limitação:</b> base estatística pequena (3 lotes oficiais). Use o "Adicionar à base" para enriquecer.
    </div>
  </div>);
});

export { CriterioSimilaridadePage };
export default CriterioSimilaridadePage;
